import React from 'react'
import { connect } from 'react-redux'
import { Navigate, NavigateFunction, useNavigate } from 'react-router-dom'

import axios from 'axios'
import classNames from 'classnames'
import { capitalize, isEmpty } from 'lodash-es'
import { bindActionCreators } from 'redux'

import { addOrdinalSuffix } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import { LDFlags, useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { CustomerInfo } from '@lyrahealth-inc/shared-app-logic/src/models/customer/Customer'
import { ProviderAdminProviderInfo } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import NavButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/navButton/NavButton'
import BootstrapContainer from '@lyrahealth-inc/ui-core/lib/molecules/layouts/bootstrapContainer/BootstrapContainer'
import CardFullWidth from '@lyrahealth-inc/ui-core/lib/molecules/layouts/cardFullWidth/CardFullWidth'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import styles from './clientDetails.module.scss'
import ClientOutcomes from './ClientOutcomes'
import * as clientDetailsAutoActions from './data/clientDetailsAutoActions'
import { getClientsClientDetailsData } from './data/clientDetailsSelectors'
import { trackClicks } from '../../../../mixpanel/mixpanelTracking'
import { ClientSessionLimitInfo } from '../../common/components/client/ClientSessionLimitInfo'
import LyraCodeProfileDisplay from '../../common/components/lyraCode/LyraCodeProfileDisplay'
import { healthPlanStates, MILITARY_ONE_SOURCE_EMPLOYER, MOS_SESSION_LABEL } from '../../common/constants/appConstants'
import { CLIENTS, LT_CLIENTS } from '../../common/constants/routingConstants'
import sessionLimitWarning from '../../common/content/sessionLimitWarning'
import sessionResetDate from '../../common/content/sessionResetDate'
import { getAuthMosApproved } from '../../data/auth/authSelectors'
import { getRequestPaymentCustomers } from '../../data/requestPayment/requestPaymentSelectors'
import { RootState } from '../../data/store'
import { getProviderDetailsData } from '../../providers/data/providerSelectors'
type ClientDetailsProps = {
  clientDetails?: any
  customers?: CustomerInfo[]
  providerDetailsData?: ProviderAdminProviderInfo
  isMOSApprovedProvider?: boolean
  actions?: any
  navigate: NavigateFunction
  flags?: LDFlags
}

export class ClientDetails extends React.Component<ClientDetailsProps> {
  canceler: any

  componentDidMount() {
    trackClicks('ClientDetails')
    const clientLyraId = this.props.clientDetails?.id
    const providerId = this.props.providerDetailsData?.lyra_id
    if (clientLyraId) {
      this.canceler = axios.CancelToken.source()
      this.props.actions.getClientDetails({ clientLyraId, cancelToken: this.canceler.token, providerId })
    }
  }

  componentWillUnmount() {
    this.canceler && this.canceler.cancel()
    this.props.actions.clearClientSelected()
  }

  _backToClients = () => {
    const { isDAProviderMigrated, isLegacyClientTransitionEnabled } = this.props.flags ?? {}
    if (!isDAProviderMigrated) {
      this.props.navigate(CLIENTS.route)
    } else if (isLegacyClientTransitionEnabled) {
      // The transition experience drives tabs from the URL — return to the legacy tab the provider came from
      this.props.navigate(`${LT_CLIENTS.route}?tab=legacy`)
    } else {
      this.props.navigate(LT_CLIENTS.route)
    }
  }

  _renderSessionTrackingPopover = (client: any) => {
    const name = capitalize(client.first_name)
    const pluralize = client.sessions_remaining === 1 ? '' : 's'
    let body = `Please remind ${name} that any sessions after their ${addOrdinalSuffix(
      client.sessions_limit,
    )} will not be covered by Lyra.`
    let sessionType = ''

    if (client.health_plan_status === 'health_plan_not_ready') {
      sessionType = 'free '
      body = `Please advise ${name} to check their Lyra Profile if they may be eligible for additional sessions using their medical plan.`
    } else if (client.health_plan_status === 'health_plan_ready') {
      sessionType = 'free '
      body =
        'After the free sessions, additional sessions will be submitted to the client’s medical plan on file with Lyra.'
    }

    let header = `${name} has ${client.sessions_remaining} ${sessionType}session${pluralize} remaining`
    if (client.health_plan_status === 'health_plan_in_use') {
      header = `${name} is using medical plan`
      body = 'Sessions will be submitted to the client’s medical plan on file with Lyra.'
    }
    return (
      <div>
        <h2 className={styles['session-tracking-header']}>{header}</h2>
        <p className={styles['session-tracking-body']}>{body}</p>
        <p className={styles['session-tracking-contact']}>
          If you have any questions please contact{' '}
          <a href='mailto:providers@lyraclinical.com'>providers@lyraclinical.com</a>.
        </p>
      </div>
    )
  }

  _getMOSDisplayName = (client: any) => {
    const lastInitial = client.last_name?.charAt(0)?.toUpperCase()
    return `${capitalize(client.first_name)}${lastInitial ? ` ${lastInitial}.` : ''}`
  }

  _renderClientInfo = (isMOSClient: boolean, isMOSApprovedProvider: boolean) => {
    const client = this.props.clientDetails
    const clientCustomer = this.props.customers?.find((customer) => customer?.value === client.employer)
    let healthPlanInfo, specialityCareInfo
    const warningThreshold = 5
    const hasEAPSessions = client.eap_session_limit !== 0
    const showBHBcount = client.open_bhb_session_count > 0 && client.open_bhb_session_limit > 0
    switch (client.health_plan_status) {
      case healthPlanStates.READY:
        healthPlanInfo = (
          <>
            Your client <span className={styles.bold}>can use</span> lyra with their health plan
            {hasEAPSessions && <> past the free EAP sessions</>}.
          </>
        )
        break
      case healthPlanStates.NOT_ELIGIBLE:
        healthPlanInfo = (
          <>
            Your client’s health plan <span className={styles.bold}>does not cover Lyra</span>. You might not be paid
            for sessions{hasEAPSessions && <> past the free EAP limit</>}.
          </>
        )
        break
      case healthPlanStates.NOT_READY:
        healthPlanInfo = (
          <>
            If you and your client intend to continue{hasEAPSessions && <> past the free EAP sessions</>}, your client{' '}
            <span className={styles.attention}>needs to enter their health plan information</span> to determine
            coverage.
          </>
        )
        break
      case healthPlanStates.HEALTH_PLAN_PAYMENT_MISSING:
        healthPlanInfo = (
          <>
            Your client has entered their health plan information but is missing their payment card for copayments.{' '}
            <span className={styles.attention}>
              Please have your client update their payment card for their health plan section to complete their profile.
            </span>{' '}
          </>
        )
    }

    if (client.customer_support_speciality_care) {
      specialityCareInfo = (
        <>
          {' '}
          If your client is high-risk, has an active substance abuse, a persistent mental health condition, has
          treatment resistant anxiety/depression, or is a minor, they may qualify for additional care.
        </>
      )
    }

    return (
      <div>
        <div className={classNames('row', styles.title)}>
          <h1 className={styles['client-full-name']}>
            {isMOSClient ? this._getMOSDisplayName(client) : client.full_name}
          </h1>
          {/* No active or inactive concept in V1 <h3>{client.isActive ? 'Active Client' : 'Inactive Client'}</h3> */}
        </div>
        <div className={classNames('row', styles.info)}>
          {!isMOSClient && (
            <div className='col-xs-12 col-sm-6'>
              <h4>Employer</h4>
              <p>{client.displayed_employer ? client.displayed_employer : 'N/A'}</p>
            </div>
          )}
          {!isMOSClient && (
            <div className='col-xs-12 col-sm-6'>
              <h4>Client Email</h4>
              <p>{client.product_email || (client.email ? client.email : 'N/A')}</p>
            </div>
          )}
          <div className='col-xs-12 col-sm-6'>
            <h4>Sessions</h4>
            {hasEAPSessions && (
              <ClientSessionLimitInfo
                displayVisitsPerIssuePerYear={clientCustomer?.display_visits_per_issue_per_year}
                displayVisitsPerIssuePerYearByProgram={clientCustomer?.display_visits_per_issue_per_year_by_program}
                sessionUsed={client.sessions_used}
                sessionLimit={client.sessions_limit}
                usedSessionsByProgram={client.sessions_used_by_program}
                EAPSessionLimitByProgram={client.sessions_limit_by_program}
                warningThreshold={warningThreshold}
                eapLabel={isMOSClient ? MOS_SESSION_LABEL : undefined}
                showSessionsUsedPerIssue={isMOSClient}
                testID={tID('ClientDetails-eap-session-count')}
              />
            )}
            {showBHBcount && (
              <p
                data-test-id='ClientDetails-bhb-session-count'
                className={classNames({
                  [styles.alert]: client.open_bhb_session_limit - client.open_bhb_session_count <= warningThreshold,
                })}
              >
                {client.open_bhb_session_count} of {client.open_bhb_session_limit} recommended health plan sessions
              </p>
            )}
            {client.speciality_care_session_count > 0 && (
              <p
                data-test-id='ClientDetails-sc-session-count'
                className={classNames({ [styles.alert]: client.sessions_remaining <= warningThreshold })}
              >
                {client.speciality_care_session_count} high-risk sessions
              </p>
            )}
          </div>
          {!isMOSClient && (
            <div className='col-xs-12 col-sm-6'>
              <h4>Coverage Information</h4>
              <p data-test-id='ClientDetails-coverage-information'>
                {healthPlanInfo}
                {specialityCareInfo}
                {sessionLimitWarning({
                  client,
                })}{' '}
                {sessionResetDate({ client })}
              </p>
            </div>
          )}
          {isMOSClient && isMOSApprovedProvider && (
            <div data-test-id='ClientDetails-MOSDetails' className='col-xs-12 col-sm-6'>
              {client.mos_id && (
                <>
                  <h4>Case Number</h4>
                  <p>{client.mos_id}</p>
                </>
              )}
              {client.authorization_number && (
                <>
                  <h4>Auth #</h4>
                  <p>{client.authorization_number}</p>
                </>
              )}
              {client.lyra_code && (
                <>
                  <h4>Lyra code</h4>
                  <p>{client.lyra_code}</p>
                </>
              )}
            </div>
          )}
          {!isMOSClient && client.friendly_lyra_code && (
            <div data-test-id='ClientDetails-lyraCode' className='col-xs-12 col-sm-6'>
              <h4>Lyra code</h4>
              <LyraCodeProfileDisplay lyraCode={client.friendly_lyra_code} />
            </div>
          )}
        </div>
      </div>
    )
  }

  render() {
    const { clientDetails } = this.props
    if (!clientDetails || isEmpty(clientDetails)) {
      return <Navigate to={CLIENTS.route} />
    }
    const isMOSClient = clientDetails.employer === MILITARY_ONE_SOURCE_EMPLOYER
    const isMOSApprovedProvider = !!this.props.isMOSApprovedProvider
    return (
      <BootstrapContainer col='col-md-10 col-md-offset-1 col-lg-8 col-lg-offset-2' style={{ overflow: 'visible' }}>
        <div style={{ marginTop: '50px' }}>
          <NavButton text='Back to Clients' styleType='back' onClick={this._backToClients} />
          <CardFullWidth className={styles['details-card']} roundCorners>
            {this._renderClientInfo(isMOSClient, isMOSApprovedProvider)}
            {!isMOSClient && (
              <ClientOutcomes
                patient_lyra_id={clientDetails.id}
                outcomes={clientDetails.outcomes}
                clientFullName={clientDetails.full_name}
                clientFirstName={clientDetails.first_name}
                consentStatus={clientDetails.email_consent}
                consentSubmitted={!clientDetails.subscribe_token}
                isRegistered={clientDetails.is_registered}
                title='Outcomes'
                learnMore
                reminder
              />
            )}
          </CardFullWidth>
        </div>
      </BootstrapContainer>
    )
  }
}

const mapStateToProps = (state: RootState) => {
  return {
    clientDetails: getClientsClientDetailsData(state),
    customers: getRequestPaymentCustomers(state),
    providerDetailsData: getProviderDetailsData(state),
    isMOSApprovedProvider: getAuthMosApproved(state),
  }
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    actions: bindActionCreators({ ...clientDetailsAutoActions }, dispatch),
  }
}

const ConnectedClientDetails = connect(mapStateToProps, mapDispatchToProps)(ClientDetails)

function ClientDetailsWithRouter(props: Omit<ClientDetailsProps, 'navigate' | 'flags'>) {
  const navigate = useNavigate()
  // The shared useFlags hook (not the raw LD consumer) so the Cypress flag shim applies here too
  const flags = useFlags()
  return <ConnectedClientDetails {...props} navigate={navigate} flags={flags} />
}

export default ClientDetailsWithRouter
